import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ParallaxHero } from "@/components/ParallaxHero";
import { MygrowLogo } from "@/components/brand/MygrowLogo";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { TOP_NAV_HEIGHT_CLASS } from "@/lib/layout";
import {
  ArrowRight,
  ClipboardCheck,
  Landmark,
  Code,
  Database,
  TestTubeDiagonal,
  GraduationCap,
  Calculator,
  Shield,
  Globe,
  Brain,
  BarChart3,
  Workflow,
  Cpu,
  CheckCircle2,
} from "lucide-react";

// Os blocos abaixo guardam **chaves** i18n, não texto pronto: a chave é a
// própria frase em inglês (`keySeparator: false`), e `t()` acontece no render.
// Assim o conteúdo da landing acompanha o idioma do navegador — que é a única
// informação disponível em `/`, onde ninguém está logado ainda.
const stats = [
  { value: "7", label: "AI Agents" },
  { value: "9", label: "SAP Activate Phases" },
  { value: "90%+", label: "Automation" },
  { value: "100%", label: "Clean Core" },
];

const methodologySteps = [
  { num: "01", phase: "Discover", caption: "Functional · Readiness Check", active: true },
  { num: "02", phase: "Prepare", caption: "Architect kickoff", active: true },
  { num: "03", phase: "Explore", caption: "Functional · Technical Architect", active: false },
  { num: "04", phase: "Realize", caption: "Data Experts · QA / Testing", active: false },
  { num: "05", phase: "Deploy", caption: "Training · Go-Live Readiness", active: false },
];

const agents = [
  {
    num: "01",
    name: "Functional",
    subtitle: "License Assessment",
    description:
      "Connects to SAP ERP via RFC, extracts usage data, classifies users by module, maps S/4HANA scope items, resolves SKUs via waterfall rule and generates a complete Bill of Materials.",
    icon: Calculator,
    capabilities: ["9 RFC Extractions", "193 TCode Catalog", "Waterfall SKU", "BOM 7 Tabs"],
    tech: "LangGraph · Claude · MinIO · 6-Node Pipeline",
  },
  {
    num: "02",
    name: "Readiness Check",
    subtitle: "Readiness Analysis",
    description:
      "Analyzes the SAP Readiness Check ZIP, parses 13+ XML files and generates an executive report with risk assessment and migration recommendations.",
    icon: ClipboardCheck,
    capabilities: ["XML Parser", "Risk Assessment", "Executive Report"],
    tech: "LangGraph · Claude · 2-Node Pipeline",
  },
  {
    num: "03",
    name: "Architect",
    subtitle: "Architecture",
    description:
      "Maps organizational structure, identifies scope items, configures business scenarios and performs functional gap analysis.",
    icon: Landmark,
    capabilities: ["Org Mapping", "Scope Items", "Gap Analysis"],
    tech: "LangGraph · Claude · RAG · 6-Node Pipeline",
  },
  {
    num: "04",
    name: "Technical Architect",
    subtitle: "Technical Architecture",
    description:
      "Designs Clean Core extensions on BTP, plans RAP / WRICEF migrations, and produces a technical gap analysis aligned to S/4HANA Cloud constraints.",
    icon: Code,
    capabilities: ["Clean Core", "BTP Extensions", "WRICEF Plan"],
    tech: "LangGraph · Claude · RAG · 5-Node Pipeline",
  },
  {
    num: "05",
    name: "Data Experts",
    subtitle: "Data Migration",
    description:
      "Generates Migration Cockpit templates, validates master and transactional data, and orchestrates cutover dry-runs with reconciliation.",
    icon: Database,
    capabilities: ["Migration Cockpit", "Cutover Dry-Run", "Reconciliation"],
    tech: "LangGraph · Claude · SAP MC · 7-Node Pipeline",
  },
  {
    num: "06",
    name: "QA / Testing",
    subtitle: "Automated Testing",
    description:
      "Authors end-to-end test scripts from Scope Items, executes regression against the Quality tenant, and triages defects back to owner agents.",
    icon: TestTubeDiagonal,
    capabilities: ["Scope-Derived Tests", "E2E Regression", "Defect Triage"],
    tech: "LangGraph · Claude · 4-Node Pipeline",
  },
  {
    num: "07",
    name: "Training",
    subtitle: "Training Materials · Go-Live Readiness",
    description:
      "Generates role-based training decks, quick reference guides and localized video scripts — produced from the exact configuration shipped to Production. Feeds the Go-Live readiness gate.",
    icon: GraduationCap,
    capabilities: ["Role-Based Decks", "Quick Reference", "Localized EN / PT", "Video Scripts"],
    tech: "LangGraph · Claude · RAG · 4-Node Pipeline",
  },
];

const platformFeatures = [
  {
    icon: Shield,
    title: "Clean Core Guaranteed",
    desc: "Every customization proposal is validated against S/4HANA Cloud release constraints before reaching the Realize phase.",
  },
  {
    icon: Workflow,
    title: "Sequential Pipeline",
    desc: "Agents share context and artifacts automatically. No prompt engineering, no manual hand-offs, no lost work.",
  },
  {
    icon: Brain,
    title: "RAG + Native SAP Tools",
    desc: "Each agent pairs Claude with a curated knowledge base and direct access to RFC, OData and Migration Cockpit APIs.",
  },
  {
    icon: BarChart3,
    title: "Full Traceability",
    desc: "Every decision logged with source scope item, TCode evidence and reviewer sign-off. Audit pack exported in one click.",
  },
  {
    icon: Globe,
    title: "Multi-Tenant, EN / PT",
    desc: "Isolated per customer tenant. Materials generated bilingually for global programs with Brazilian operations.",
  },
  {
    icon: Cpu,
    title: "Key-Users Validate",
    desc: "The platform does the work. Key-users validate — they don't execute. Hand-off to business as you go, not at the end.",
  },
];

const readinessChecklist = [
  "Scope coverage vs Bill of Materials — 100%",
  "Clean Core compliance on all WRICEF items",
  "Cutover dry-run reconciled without variance",
  "Training materials signed off per role",
];

const readinessGates = [
  { name: "Discover · Scope locked", status: "Pass" },
  { name: "Prepare · Readiness Report", status: "Pass" },
  { name: "Explore · Functional + Tech gap", status: "Pass" },
  { name: "Realize · QA regression", status: "Running" },
  { name: "Deploy · Training sign-off", status: "Pending" },
] as const;

type GateStatus = (typeof readinessGates)[number]["status"];

const gateStyles: Record<GateStatus, string> = {
  Pass: "bg-[rgba(39,161,85,0.15)] text-[#5cc77f]",
  Running: "bg-[rgba(255,200,61,0.15)] text-[#ffc83d]",
  Pending: "bg-white/[0.06] text-white/45",
};

function FadeIn({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function SectionHead({ overline, title, sub }: { overline: string; title: string; sub: string }) {
  return (
    <FadeIn className="text-center mb-12 max-w-xl mx-auto">
      <div className="font-mono text-[11px] font-semibold tracking-[0.16em] uppercase text-[#ffc83d] mb-3.5">
        {overline}
      </div>
      <h2 className="font-brand text-[38px] leading-[44px] font-extrabold tracking-[-0.02em] text-white mb-3.5">
        {title}
      </h2>
      <p className="text-[15px] text-white/65 leading-relaxed">{sub}</p>
    </FadeIn>
  );
}

export default function Index() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen overflow-x-hidden scroll-smooth" style={{ backgroundColor: "#08254c" }}>
      {/* ═══ TOP NAV ═══ */}
      <nav
        className={cn(
          "fixed top-0 inset-x-0 z-50 bg-[#08254c]/80 backdrop-blur-md border-b border-white/10",
          TOP_NAV_HEIGHT_CLASS,
        )}
      >
        <div className="h-full flex items-center px-8 gap-7">
          <MygrowLogo className="h-[38px] w-auto shrink-0 text-white" />
          <div className="hidden md:flex items-center gap-[22px] ml-5">
            <a href="#platform" className="text-[13px] font-medium text-white/55 hover:text-white transition-colors">
              {t("Platform")}
            </a>
            <a href="#agents" className="text-[13px] font-medium text-white/55 hover:text-white transition-colors">
              {t("Agents")}
            </a>
            <a href="#methodology" className="text-[13px] font-medium text-white/55 hover:text-white transition-colors">
              {t("Methodology")}
            </a>
            <a href="#readiness" className="text-[13px] font-medium text-white/55 hover:text-white transition-colors">
              {t("Go-Live Readiness")}
            </a>
          </div>
          <div className="flex-1" />
          <Link to="/login" className="text-[13px] text-white/75 hover:text-white transition-colors mr-2">
            {t("Sign in")}
          </Link>
          <Link to="/login">
            <Button variant="action" size="sm" className="h-9 text-[13px] font-semibold gap-2">
              {t("Get Started")} <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <ParallaxHero reserveTopNav>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 text-center max-w-3xl mx-auto px-6"
        >
          {/* Pill */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-sm mb-[22px]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ffc83d] shadow-[0_0_10px_#ffc83d]" />
            <span className="text-[11px] font-medium text-white/65">
              {t("Agentic AI · SAP Activate · Clean Core Guaranteed")}
            </span>
          </div>

          {/* H1 — Nunito Sans brand font */}
          <h1 className="font-brand text-[40px] leading-[46px] md:text-[56px] md:leading-[62px] font-extrabold tracking-[-0.025em] text-white">
            {t("SAP S/4HANA Cloud Migration")}{" "}
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: "linear-gradient(to right, #ffffff, #ffc83d)" }}
            >
              {t("Powered by AI")}
            </span>
          </h1>

          <p className="mt-5 text-[17px] text-white/75 leading-relaxed max-w-2xl mx-auto">
            {t(
              "Seven AI agents in a sequential pipeline orchestrate every step of the migration — from License Assessment to Go-Live — with full traceability and Clean Core guaranteed.",
            )}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 mb-14">
            <Link to="/login">
              <Button variant="action" size="lg" className="h-[46px] px-[22px] text-sm font-semibold gap-2">
                {t("Start Migration")} <ArrowRight className="w-3.5 h-3.5" />
              </Button>
            </Link>
            <a href="#agents">
              <Button
                variant="outline"
                size="lg"
                className="h-[46px] px-[22px] text-sm font-semibold gap-2 bg-transparent border-[#ffc83d]/60 text-[#ffc83d] hover:bg-[#ffc83d]/10 hover:text-[#ffc83d]"
              >
                {t("Meet the Agents")}
              </Button>
            </a>
          </div>

          {/* Stat tiles */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto"
          >
            {stats.map((s) => (
              <div
                key={s.label}
                className="py-5 px-5 rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm"
              >
                <p className="font-brand text-[36px] font-extrabold tracking-[-0.02em] leading-none" style={{ color: "#ffc83d" }}>
                  {s.value}
                </p>
                <p className="text-[11px] font-semibold text-white/55 uppercase tracking-[0.08em] mt-1.5">
                  {t(s.label)}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </ParallaxHero>

      {/* ═══ METHODOLOGY ═══ */}
      <section id="methodology" className="py-20 px-6 md:px-10 border-b border-white/5" style={{ backgroundColor: "#08254c" }}>
        <div className="max-w-6xl mx-auto">
          <SectionHead
            overline={t("Methodology")}
            title={t("Sequential pipeline aligned with SAP Activate")}
            sub={t("Each phase produces validated artifacts that feed the next agent. Key-users validate, not execute.")}
          />

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
            {methodologySteps.map((step, i) => (
              <FadeIn key={step.num} delay={i * 0.06}>
                <div
                  className={`rounded-xl border p-5 h-full ${
                    step.active
                      ? "border-[#ffc83d]/40 bg-[#ffc83d]/[0.08]"
                      : "border-white/10 bg-white/[0.03]"
                  }`}
                >
                  <div className={`font-mono text-[11px] font-semibold tracking-wider ${step.active ? "text-[#ffc83d]" : "text-white/35"}`}>
                    {step.num} · {t("Phase")}
                  </div>
                  <h4 className="text-base font-bold tracking-tight text-white mt-2 mb-1.5">{t(step.phase)}</h4>
                  <p className="text-xs text-white/55 leading-snug">{t(step.caption)}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AGENTS ═══ */}
      <section id="agents" className="py-20 px-6 md:px-10 border-b border-white/5" style={{ backgroundColor: "#061e3d" }}>
        <div className="max-w-6xl mx-auto">
          <SectionHead
            overline={t("The Seven Agents")}
            title={t("Senior specialists, orchestrated end-to-end")}
            sub={t("Each agent is a senior specialist in its domain, equipped with RAG, LLM and native SAP tools. Agents share context and artifacts automatically.")}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agents.map((agent, i) => (
              <FadeIn
                key={agent.num}
                delay={i * 0.06}
                className={agent.num === "07" ? "md:col-span-2" : ""}
              >
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#ffc83d]/30 transition-colors h-full">
                  <div className="flex items-start gap-3.5 mb-3">
                    <span className="font-mono text-[22px] font-bold text-white/15 min-w-[36px]">
                      {agent.num}
                    </span>
                    <div
                      className="w-11 h-11 rounded-[10px] flex items-center justify-center shrink-0"
                      style={{
                        backgroundColor: "rgba(255,200,61,0.1)",
                        border: "1px solid rgba(255,200,61,0.2)",
                      }}
                    >
                      <agent.icon className="w-[22px] h-[22px]" style={{ color: "#ffc83d" }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-bold tracking-tight text-white">{agent.name}</h3>
                      <div className="font-mono text-[11px] text-white/45 mt-0.5">{t(agent.subtitle)}</div>
                    </div>
                  </div>

                  <p className="text-[13px] text-white/65 leading-[1.55] mb-3">{t(agent.description)}</p>

                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {agent.capabilities.map((cap) => (
                      <span
                        key={cap}
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-white/10 bg-white/5 text-[10px] font-medium text-white/55"
                      >
                        <span className="w-[5px] h-[5px] rounded-full bg-[#27a155]" />
                        {t(cap)}
                      </span>
                    ))}
                  </div>

                  <p className="font-mono text-[10px] tracking-wide text-white/30">{t(agent.tech)}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ PLATFORM ═══ */}
      <section id="platform" className="py-20 px-6 md:px-10 border-b border-white/5" style={{ backgroundColor: "#08254c" }}>
        <div className="max-w-6xl mx-auto">
          <SectionHead
            overline={t("Platform")}
            title={t("Built for enterprise SAP programs")}
            sub={t("Tenant isolation, Clean Core enforcement, full traceability — ready for audit from day one.")}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {platformFeatures.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.06}>
                <div className="p-6 rounded-xl border border-white/10 bg-white/[0.03] hover:border-[#ffc83d]/20 transition-colors h-full">
                  <div
                    className="w-11 h-11 rounded-[10px] flex items-center justify-center mb-3.5"
                    style={{ backgroundColor: "rgba(255,200,61,0.1)" }}
                  >
                    <item.icon className="w-[22px] h-[22px]" style={{ color: "#ffc83d" }} />
                  </div>
                  <h4 className="text-base font-bold text-white mb-2">{t(item.title)}</h4>
                  <p className="text-[13px] text-white/60 leading-[1.55]">{t(item.desc)}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ GO-LIVE READINESS ═══ */}
      <section id="readiness" className="py-20 px-6 md:px-10 border-b border-white/5" style={{ backgroundColor: "#061e3d" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <FadeIn>
              <div className="font-mono text-[11px] font-semibold tracking-[0.16em] uppercase text-[#ffc83d] mb-3.5">
                {t("Go-Live Readiness")}
              </div>
              <h3 className="font-brand text-[34px] leading-[40px] font-extrabold tracking-[-0.02em] text-white mb-4">
                {t("One score. Five gates. Zero surprises.")}
              </h3>
              <p className="text-sm text-white/70 leading-[1.65] mb-[18px]">
                {t("Go-Live Readiness aggregates every artifact from the seven agents into a single executive score. Each gate maps to an SAP Activate quality milestone — green, amber, red, in real time.")}
              </p>
              <div className="flex flex-col gap-2.5">
                {readinessChecklist.map((item) => (
                  <div key={item} className="flex items-start gap-2.5 text-[13px] text-white/80">
                    <CheckCircle2 className="w-4 h-4 text-[#27a155] shrink-0 mt-0.5" />
                    <span>{t(item)}</span>
                  </div>
                ))}
              </div>
            </FadeIn>

            <FadeIn delay={0.15}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-7">
                <div className="flex justify-between items-center mb-[18px]">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/50">
                      {/* Nome fictício do mockup de score. Passa por `t()` como
                          o resto para o gate não precisar de exceção; o valor é
                          idêntico nos três idiomas, como o jargão SAP. */}
                      {t("ACME Corp S/4HANA")}
                    </div>
                    <div className="text-xl font-bold text-white mt-1">{t("Readiness Score")}</div>
                  </div>
                  <div className="font-brand text-[44px] font-extrabold tracking-[-0.02em]" style={{ color: "#ffc83d" }}>
                    82
                  </div>
                </div>

                <div className="mb-[22px]">
                  <div className="flex justify-between text-xs text-white/55 mb-1.5">
                    <span>{t("Overall")}</span>
                    <span>82 / 100</span>
                  </div>
                  <div className="h-2 bg-white/[0.08] rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: "82%" }}
                      viewport={{ once: true, margin: "-80px" }}
                      transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
                      className="h-full rounded-full"
                      style={{ background: "linear-gradient(90deg, #27a155, #ffc83d)" }}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {readinessGates.map((gate) => (
                    <div
                      key={gate.name}
                      className="flex justify-between items-center px-3.5 py-2.5 rounded-lg border border-white/10 text-xs"
                    >
                      <span className="font-medium text-white/90">{t(gate.name)}</span>
                      <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full ${gateStyles[gate.status]}`}>
                        {t(gate.status)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 px-6 md:px-10" style={{ backgroundColor: "#08254c" }}>
        <FadeIn className="max-w-4xl mx-auto">
          <div
            className="rounded-3xl text-center py-[60px] px-10 md:px-14"
            style={{
              background: "linear-gradient(135deg, #08254c, #061e3d)",
              border: "1px solid rgba(255,200,61,0.2)",
            }}
          >
            <div className="font-mono text-[11px] font-semibold tracking-[0.16em] uppercase text-[#ffc83d] mb-3.5">
              {t("Ready when you are")}
            </div>
            <h2 className="font-brand text-3xl md:text-[34px] leading-[40px] font-extrabold tracking-[-0.015em] text-white mb-4">
              {t("Ready to transform your migration?")}
            </h2>
            <p className="text-[15px] text-white/70 max-w-xl mx-auto mb-7">
              {t("Bring your ERP tenant, your Readiness Check, and your program team. myGROW brings the specialists.")}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/login">
                <Button variant="action" size="lg" className="h-[46px] px-[22px] text-sm font-semibold gap-2">
                  {t("Book a Walkthrough")} <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
              <Button
                variant="outline"
                size="lg"
                className="h-[46px] px-[22px] text-sm font-semibold gap-2 bg-transparent border-[#ffc83d]/60 text-[#ffc83d] hover:bg-[#ffc83d]/10 hover:text-[#ffc83d]"
              >
                {t("Download Overview")}
              </Button>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="px-6 md:px-10 pt-12 pb-8 border-t border-white/[0.06]" style={{ backgroundColor: "#051935" }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start gap-10 flex-wrap">
            <div className="max-w-[280px]">
              <MygrowLogo className="h-[38px] w-auto text-white" />
              <p className="text-xs text-white/50 leading-[1.6] mt-2.5">
                {t("myGROW is an Exed Consulting product. SAP S/4HANA Cloud, Public Edition migration — agentic AI for the full lifecycle.")}
              </p>
            </div>

            <div>
              <h5 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/50 mb-3">
                {t("Platform")}
              </h5>
              <a href="#agents" className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors">{t("Agents")}</a>
              <a href="#methodology" className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors">{t("Methodology")}</a>
              <a href="#readiness" className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors">{t("Go-Live Readiness")}</a>
              <a href="#platform" className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors">{t("Security")}</a>
            </div>

            <div>
              <h5 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-white/50 mb-3">
                {t("Company")}
              </h5>
              {/* `About Exed` e `Customers` levavam ao mesmo lugar — isto é,
                  a nenhum. Fundidos num link só para a home institucional.
                  `rel="noopener"` porque `target="_blank"` sem ele dá à página
                  aberta acesso ao `window.opener` desta. */}
              <a
                href="https://exedconsulting.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors"
              >
                {t("About Exed")}
              </a>
              <a
                href="https://exedconsulting.com/sobre-nos/carreiras/"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors"
              >
                {t("Careers")}
              </a>
              <a
                href="https://exedconsulting.com/contato/"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-1 text-[13px] text-white/65 hover:text-[#ffc83d] transition-colors"
              >
                {t("Contact")}
              </a>
            </div>
          </div>

          <div className="border-t border-white/[0.06] mt-8 pt-4 text-center font-mono text-[11px] text-white/35">
            {t("© 2026 Exed Consulting · myGROW™ · SAP® and S/4HANA® are trademarks of SAP SE")} ·{" "}
            <a href="/THIRD-PARTY-NOTICES.txt" className="hover:text-[#ffc83d] transition-colors">
              {t("Third-Party Notices")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
